import clsx from 'clsx';
import type * as React from 'react';

import { Description } from '../Description';
import { type ImageProps, ProgressiveImage } from '../Image';
import { Layout } from '../Page/Layout';

/**
 * Intro section for a page.
 */
export function PageIntro(props: { kind?: 'split' | 'centered'; children: React.ReactNode }) {
    const { kind = 'split', ...rest } = props;

    return kind === 'split' ? <PageIntroSplit {...rest} /> : <PageIntroCentered {...rest} />;
}

/**
 * Page intro `centered` specific components.
 */
export function PageIntroCentered(props: { className?: string; children: React.ReactNode }) {
    return (
        <Layout
            kind="centered"
            slotClassName="flex items-center justify-center pt-8 text-center md:pt-14"
        >
            <div className="flex max-w-[26rem] flex-col gap-2">{props.children}</div>
        </Layout>
    );
}

/**
 * Page intro `split` specific components.
 */
export function PageIntroSplit(props: { children: React.ReactNode }) {
    return <div className={'flex items-center gap-10'}>{props.children}</div>;
}

export function PageIntroSplitMain(props: { className?: string; children: React.ReactNode }) {
    return <div {...props} className={clsx('flex flex-1 flex-col gap-4', props.className)} />;
}

export function PageIntroSplitSide(props: React.ComponentPropsWithoutRef<'div'>) {
    return (
        <div
            {...props}
            className={clsx('flex flex-1 self-stretch max-lg:hidden', props.className)}
        />
    );
}

export function PageIntroSideIllustration(props: {
    src: ImageProps['src'];
    width: number;
    height: number;
}) {
    const { src, width, height } = props;
    return (
        <PageIntroSplitSide style={{ aspectRatio: `${width} / ${height}` }}>
            <ProgressiveImage
                src={src}
                className="size-full object-cover"
                width={width}
                height={height}
            />
        </PageIntroSplitSide>
    );
}

/**
 * Page intro shared components.
 */
export function PageIntroHeader(props: { className?: string; children: React.ReactNode }) {
    return <div {...props} className={clsx('flex items-center gap-4', props.className)} />;
}

export function PageIntroHeaderIcon(props: { children: React.ReactNode }) {
    return <div {...props} className="m-nudge-icon shrink-0" />;
}

export function PageIntroHeading(props: {
    children: React.ReactNode;
    className?: string;
    size?: 'large' | 'medium';
}) {
    const { size = 'large', className, ...rest } = props;

    return <h1 {...rest} className={clsx(pageIntroHeadingTextStyle[size], className)} />;
}
const pageIntroHeadingTextStyle = {
    large: 'text-ui-page-title',
    medium: 'text-ui-heading-large',
};

export function PageIntroDescription(props: { children: React.ReactNode; className?: string }) {
    return <Description {...props} />;
}

export function PageIntroActions(props: { children: React.ReactNode }) {
    return <div {...props} className="flex flex-wrap gap-4" />;
}
